import React, { useCallback, useEffect } from 'react';
import { connect } from 'react-redux';
import { RouteProps, NavLink, useHistory } from 'react-router-dom';
import { cond, none } from 'ramda';
import { StepIconProps } from '@material-ui/core/StepIcon';
import Step from '@material-ui/core/Step';
import Stepper from '@material-ui/core/Stepper';
import StepConnector from '@material-ui/core/StepConnector';
import StepLabel from '@material-ui/core/StepLabel';
import Typography from '@material-ui/core/Typography';
import { ShopCartItem } from 'store/ducks/shopCart/types';
import Icon from 'common/components/Icon';
import { routes } from './common/routes';
import {
    makeColorlibStepIconStyles,
    makeStepConnectorClasses,
    makeStepLabelClasses,
    makeStepperClasses,
    makeTitleClasses,
} from 'styles/pages/checkout';
import { shopCartOperations } from 'store/ducks/shopCart';
import { checkoutOperations } from 'store/ducks/checkout';
import EmptyCart from './components/EmptyCart';
import { repositories } from 'api';
import Box from '@material-ui/core/Box';
import { StoreBasicDataApiStructure } from 'common/types';
import Alert from '@material-ui/lab/Alert';

type Props = {
    items: Array<ShopCartItem>;
    fetchCart: Function;
    isPromoter?: boolean;
    setCheckoutValues: Function;
};

type MappingType = {
    me: StoreBasicDataApiStructure<repositories.MeType>;
    shopCart: Array<ShopCartItem>;
};

const ColorlibStepIcon = (props: StepIconProps) => {
    const classes = makeColorlibStepIconStyles();
    const { active, completed, icon } = props;

    const orderConfirmed = window.location.pathname.match(/^\/checkout\/confirm\/?$/g);
    const linkAllowed = !orderConfirmed && (active || completed);

    const icons: { [index: string]: { icon: React.ReactElement; url: string } } = {
        1: { icon: <Icon.CheckoutCart />, url: routes.CART },
        2: { icon: <Icon.CheckoutOrder />, url: routes.ORDER },
        3: { icon: <Icon.CheckoutSummary />, url: routes.SUMMARY },
        4: { icon: <Icon.CheckoutConfirm />, url: routes.CONFIRM },
    };

    const className = `${classes.root} ${active ? classes.active : ''} ${completed ? classes.completed : ''
        }`;

    return linkAllowed ? (
        <NavLink className={className} to={icons[String(icon)].url}>
            {icons[String(icon)].icon}
        </NavLink>
    ) : (
        <Box className={className}>{icons[String(icon)].icon}</Box>
    );
};

const getStep = (location: RouteProps['location']) => {
    const pathExist = (path: string) => Boolean(location?.pathname.match(path));

    return cond([
        [() => pathExist(routes.CART), () => 0],
        [() => pathExist(routes.ORDER), () => 1],
        [() => pathExist(routes.SUMMARY), () => 2],
        [() => pathExist(routes.CONFIRM), () => 3],
    ])();
};

const steps: Array<{ subtitle: string; title: string }> = [
    { subtitle: 'Skompletuj zamówienie.', title: 'Twój koszyk' },
    { subtitle: 'Uzupełnij dane zamówienia.', title: 'Dane zamówienia' },
    { subtitle: 'Sprawdź zamówienie.', title: 'Podsumowanie' },
    { subtitle: 'Oczekuj na realizację.', title: 'Potwierdzenie' },
];

const Checkout: React.FC<Props & RouteProps> = ({
    fetchCart,
    location,
    isPromoter,
    items,
    setCheckoutValues,
}) => {
    const { push } = useHistory();

    const activeStep = getStep(location);
    const stepConnectorClasses = makeStepConnectorClasses();
    const stepLabelClasses = makeStepLabelClasses();
    const stepperClasses = makeStepperClasses();
    const titleClasses = makeTitleClasses();
    const productsCart = items.length;

    useEffect(() => {
        const cartRedirect = window.location.pathname.match(/^\/checkout\/?$/g);

        cartRedirect && push('/checkout/cart');

        fetchCart();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const renderSteps = useCallback(
        ({ subtitle, title }, index) => {
            const color = index === activeStep ? 'secondary' : 'primary';

            return (
                <Step key={title} className={stepLabelClasses.stepContainer}>
                    <StepLabel
                        StepIconComponent={ColorlibStepIcon}>
                        <Box className={stepLabelClasses.labelContainerDesktop}>
                            <Typography classes={titleClasses} color={color} component="span">
                                {title}
                            </Typography>
                            <br />
                            <Typography component="span" variant="caption">
                                {subtitle}
                            </Typography>
                        </Box>
                    </StepLabel>
                </Step>
            );
        },
        [activeStep, titleClasses],
    );

    const renderMobileLabel = useCallback(
        ({ subtitle, title }, index) => {
            const color = index === activeStep ? 'secondary' : 'primary';

            return (
                <Box textAlign='center' width={'100%'}>
                    {index === activeStep && (
                        <Box className={stepLabelClasses.labelContainerMobile}>
                            <Typography classes={titleClasses} color={color} component="span">
                                {title}
                            </Typography>
                            <br />
                            <Typography component="span" variant="caption">
                                {subtitle}
                            </Typography>
                        </Box>
                    )}
                </Box>
            );
        },
        [activeStep, titleClasses],
    );

    return productsCart ? (
        <>
            <Stepper
                alternativeLabel
                activeStep={activeStep}
                classes={stepperClasses}
                connector={<StepConnector classes={stepConnectorClasses} />}
            >
                {steps.map(renderSteps)}
                {steps.map(renderMobileLabel)}
            </Stepper>
            {isPromoter ? <Alert severity="info">Zamówienie promotorskie</Alert> : null}
        </>
    ) : (
        <EmptyCart setCheckoutValues={setCheckoutValues} />
    );
};

const mapStateToProps = (state: MappingType) => ({
    isPromoter: state.me.data?.isPromoter,
    items: state.shopCart,
});

const mapDispatchToProps = {
    fetchCart: shopCartOperations.fetchCart.request,
    setCheckoutValues: checkoutOperations.setCheckoutValues,
};

export default connect(mapStateToProps, mapDispatchToProps)(Checkout);
